(function () {
  const DESKTOP_MEDIA = window.matchMedia("(min-width: 990px)");
  const ANIMATION_DURATION = 700;

  class StickyContentGallery {
    constructor(section) {
      this.section = section;
      this.root = section.querySelector(".sticky-content-gallery");
      this.container = section.querySelector(
        ".sticky-content-gallery__container"
      );
      this.imagesTrack = section.querySelector(
        ".sticky-content-gallery__images-track"
      );
      this.images = Array.from(
        section.querySelectorAll(".sticky-content-gallery__image")
      );
      this.contents = Array.from(
        section.querySelectorAll(".sticky-content-gallery__content")
      );
      this.avatars = Array.from(
        section.querySelectorAll(".sticky-content-gallery__avatar")
      );
      this.header = section.querySelector(".sticky-content-gallery-header");

      if (
        !this.root ||
        !this.container ||
        !this.imagesTrack ||
        !this.images.length ||
        !this.contents.length
      ) {
        return;
      }

      this.currentIndex = 0;
      this.isDesktop = DESKTOP_MEDIA.matches;
      this.destroyed = false;
      this.scrollTicking = false;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onAvatarFocus = this.onAvatarFocus.bind(this);

      this.init();
    }

    init() {
      if (this.section.dataset.stickyContentGalleryInitialized === "true") {
        return;
      }

      this.section.dataset.stickyContentGalleryInitialized = "true";

      this.setHeaderOffsetVar();
      this.setSectionMetrics();
      this.goTo(0, true);

      window.addEventListener("scroll", this.onScroll, { passive: true });
      window.addEventListener("resize", this.onResize);
      window.addEventListener("orientationchange", this.onResize);

      this.avatars.forEach((avatar) => {
        avatar.addEventListener("focus", this.onAvatarFocus);
      });

      this.section.addEventListener("keydown", this.onKeydown);

      this.onScroll();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      window.removeEventListener("scroll", this.onScroll, { passive: true });
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("orientationchange", this.onResize);

      this.avatars.forEach((avatar) => {
        avatar.removeEventListener("focus", this.onAvatarFocus);
      });

      this.section.removeEventListener("keydown", this.onKeydown);

      delete this.section.dataset.stickyContentGalleryInitialized;
    }

    onResize() {
      this.isDesktop = DESKTOP_MEDIA.matches;
      this.setHeaderOffsetVar();
      this.setSectionMetrics();
      this.goTo(this.currentIndex, true);
      this.onScroll();
    }

    onScroll() {
      if (this.scrollTicking) return;

      this.scrollTicking = true;

      requestAnimationFrame(() => {
        this.scrollTicking = false;
        this.updateFromScroll();
      });
    }

    onKeydown(event) {
      if (!this.isDesktop) return;

      if (event.key === "ArrowDown") {
        const maxIndex = this.getMaxIndex();
        if (this.currentIndex < maxIndex) {
          event.preventDefault();
          this.goTo(this.currentIndex + 1);
        }
      }

      if (event.key === "ArrowUp") {
        if (this.currentIndex > 0) {
          event.preventDefault();
          this.goTo(this.currentIndex - 1);
        }
      }
    }

    onAvatarFocus(event) {
      const index = Number(event.currentTarget.dataset.index);
      if (Number.isInteger(index)) {
        this.goTo(index);
      }
    }

    getHeaderHeight() {
      if (!this.header) return 0;

      return (
        this.header.offsetHeight +
        parseFloat(getComputedStyle(this.header).marginBottom || 0)
      );
    }

    setHeaderOffsetVar() {
      if (!this.root) return;

      this.root.style.setProperty(
        "--sticky-content--header-height",
        `${this.getHeaderHeight()}px`
      );
    }

    getStickyTop() {
      const topVar = getComputedStyle(this.container)
        .getPropertyValue("--sticky-content-gallery-top")
        .trim();

      if (topVar.endsWith("px")) {
        const parsed = parseFloat(topVar);
        return Number.isNaN(parsed) ? 0 : parsed;
      }

      const top = parseFloat(getComputedStyle(this.container).top || 0);
      return Number.isNaN(top) ? 0 : top;
    }

    getMaxIndex() {
      return Math.max(
        0,
        Math.min(this.images.length, this.contents.length) - 1
      );
    }

    getVisibleHeight() {
      const stickyTop = this.getStickyTop();
      return Math.max(0, window.innerHeight - stickyTop);
    }

    setSectionMetrics() {
      if (!this.isDesktop) {
        this.root.style.removeProperty("min-height");
        this.root.style.removeProperty("height");
        this.root.style.removeProperty("--sticky-content-gallery-step-height");
        return;
      }

      const visibleHeight = this.getVisibleHeight();
      const headerHeight = this.getHeaderHeight();
      const steps = this.getMaxIndex() + 1;
      const totalHeight = headerHeight + visibleHeight * steps;

      this.root.style.minHeight = `${totalHeight}px`;
      this.root.style.height = "auto";
      this.root.style.setProperty(
        "--sticky-content-gallery-step-height",
        `${visibleHeight}px`
      );
    }

    updateFromScroll() {
      if (!this.isDesktop) {
        this.goTo(0, true);
        return;
      }

      const maxIndex = this.getMaxIndex();

      if (maxIndex <= 0) {
        this.goTo(0, true);
        return;
      }

      const stickyTop = this.getStickyTop();
      const visibleHeight = this.getVisibleHeight();
      const headerHeight = this.getHeaderHeight();
      const sectionRect = this.section.getBoundingClientRect();
      const sectionTop = window.scrollY + sectionRect.top;

      const start = sectionTop + headerHeight - stickyTop;
      const distance = visibleHeight * maxIndex;
      const rawProgress = (window.scrollY - start) / distance;
      const progress = Math.max(0, Math.min(1, rawProgress));
      const nextIndex = Math.round(progress * maxIndex);

      this.goTo(nextIndex);
    }

    goTo(index, instant = false) {
      const maxIndex = this.getMaxIndex();
      const nextIndex = Math.max(0, Math.min(index, maxIndex));

      if (nextIndex === this.currentIndex && !instant) return;

      this.currentIndex = nextIndex;

      if (this.isDesktop) {
        if (instant) {
          const prevTransition = this.imagesTrack.style.transition;
          this.imagesTrack.style.transition = "none";
          this.setTrackPosition(nextIndex);
          this.imagesTrack.offsetHeight;
          this.imagesTrack.style.transition = prevTransition || "";
        } else {
          this.setTrackPosition(nextIndex);
        }
      }

      this.updateContents(nextIndex, instant);
      this.updateAvatars(nextIndex);
    }

    setTrackPosition(index) {
      const stepHeight =
        parseFloat(
          getComputedStyle(this.root).getPropertyValue(
            "--sticky-content-gallery-step-height"
          )
        ) || window.innerHeight;

      const translateY = -(index * stepHeight);
      this.imagesTrack.style.transform = `translate3d(0, ${translateY}px, 0)`;
    }

    updateContents(activeIndex, instant = false) {
      this.contents.forEach((content, index) => {
        const isActive = index === activeIndex;
        content.classList.toggle("active", isActive);
        content.classList.remove("animated");

        if (isActive) {
          if (instant) {
            content.classList.add("animated");
          } else {
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                content.classList.add("animated");
              });
            });
          }
        }
      });
    }

    updateAvatars(activeIndex) {
      this.avatars.forEach((avatar, index) => {
        avatar.classList.toggle("active", index === activeIndex);
        avatar.setAttribute(
          "aria-current",
          index === activeIndex ? "true" : "false"
        );
      });
    }
  }

  let instances = [];

  const initStickyContentGalleries = () => {
    instances.forEach((instance) => instance.destroy());
    instances = [];

    const sections = document.querySelectorAll(
      ".sticky-content-gallery-section"
    );

    sections.forEach((section) => {
      const instance = new StickyContentGallery(section);
      if (instance.root) {
        instances.push(instance);
      }
    });
  };

  document.addEventListener("DOMContentLoaded", initStickyContentGalleries);
  document.addEventListener("shopify:section:load", initStickyContentGalleries);
  document.addEventListener(
    "shopify:section:reorder",
    initStickyContentGalleries
  );
  document.addEventListener(
    "shopify:section:unload",
    initStickyContentGalleries
  );
})();
